import { useCallback, useMemo } from 'react';

import { MY_DAY_STEP_STATUS } from '../../../../common/utils/myDay/driverDaySteps';
import { STEP_SYNC_STATE, getQueuedStepEntries } from '../stepSyncState';
import { getReturnKeyStops } from './returnKeyItems';
import { countLabel, getStopLabels } from './stopLabels';

export const RETURN_KEYS_STEP_KEY = 'returnKeys';

const COUNTABLE_STEP_STATUSES = [MY_DAY_STEP_STATUS.DONE, MY_DAY_STEP_STATUS.TODO, MY_DAY_STEP_STATUS.BLOCKED];

/**
 * The End of day step list shared by End of day and its step screens: the
 * visible end-of-day steps of driverDaySteps. "Return keys and access passes"
 * is never required, so the next action stays as driverDaySteps defines it; it
 * does count in `progress` (End of day and the Home run card) and becomes the
 * current step when it is the first open one.
 *
 * Returns:
 * - steps: rows ({ key, label, description, status, required, syncState, ... });
 *   syncState is STEP_SYNC_STATE.PENDING / FAILED while a queued offline
 *   confirmation or checklist for the step waits to sync, else null
 * - progress: { doneCount, totalCount } over these steps, counted like
 *   day.endOfDayProgress (training-locked and info steps left out)
 * - currentKey: the step to do now (first open required step, or the keys
 *   reminder before it) once the stops are finished; null otherwise
 * - getStepAfter(key): the next open step after `key`, or null
 * - getStepPosition(key): { number, total } for "Step 2 of 6"
 * - returnKeyStops ([{ docket, items }]), areKeysReturned
 * - clockOutWarning: "Not done yet: …. Couldn't complete: Stop 3 <site> (<reason>).
 *   You can still clock out." while stops, steps before Clock out or another of
 *   today's runs are open or stops were marked "Couldn't complete", else null
 *   (Clock out never locks, decision 10)
 */
const useEndOfDaySteps = (day) => {
	const {
		serviceRun,
		sortedDockets,
		visibleEndSteps,
		stepByKey,
		nextAction,
		scopedQueue,
		todayDateKey,
		completedStopsCount,
		couldNotCompleteDockets,
		allStopsHandled,
		allAssignedRunsComplete,
	} = day;
	const returnKeyStops = useMemo(() => getReturnKeyStops(sortedDockets), [sortedDockets]);
	const areKeysReturned = stepByKey[RETURN_KEYS_STEP_KEY]?.status === MY_DAY_STEP_STATUS.DONE;

	const steps = useMemo(() => {
		// Every queued entry counts: one failed entry marks the step failed.
		const getSyncState = (step) => {
			const entries = getQueuedStepEntries(step, { queue: scopedQueue, serviceRunId: serviceRun?.id, todayDateKey });
			if (!entries.length) {
				return null;
			}

			return entries.some(entry => entry.failureCount) ? STEP_SYNC_STATE.FAILED : STEP_SYNC_STATE.PENDING;
		};

		return visibleEndSteps.map(step => ({ ...step, syncState: getSyncState(step) }));
	}, [scopedQueue, serviceRun?.id, todayDateKey, visibleEndSteps]);

	const progress = useMemo(() => {
		const countable = steps.filter(step => !step.trainingUnavailable && COUNTABLE_STEP_STATUSES.includes(step.status));
		return {
			doneCount: countable.filter(step => step.status === MY_DAY_STEP_STATUS.DONE).length,
			totalCount: countable.length,
		};
	}, [steps]);

	const currentKey = useMemo(() => {
		if (nextAction?.type !== 'end') {
			return null;
		}

		return steps.find(step => (
			step.status === MY_DAY_STEP_STATUS.TODO &&
			(step.required || step.key === RETURN_KEYS_STEP_KEY)
		))?.key || null;
	}, [nextAction?.type, steps]);

	// List what is still open, and the stops the driver could not complete, so
	// they clock out knowingly.
	const remainingStops = sortedDockets.length - completedStopsCount - couldNotCompleteDockets.length;
	const clockOutWarning = useMemo(() => {
		const unfinished = steps
			.filter(step => step.key !== 'clockOut' && step.status === MY_DAY_STEP_STATUS.TODO)
			.map(step => step.label);
		if (remainingStops > 0) {
			unfinished.unshift(countLabel(remainingStops, 'stop', 'stops'));
		}
		// This run is finished but today's other assigned runs are not.
		if (allStopsHandled && !allAssignedRunsComplete) {
			unfinished.unshift('another run');
		}
		const couldNotComplete = couldNotCompleteDockets.map((docket) => {
			const { site, position } = getStopLabels(docket);
			return `Stop ${sortedDockets.indexOf(docket) + 1} ${site || position} (${docket.couldNotComplete.reason})`;
		});

		const parts = [
			unfinished.length ? `Not done yet: ${unfinished.join(', ')}.` : null,
			couldNotComplete.length ? `Couldn't complete: ${couldNotComplete.join(', ')}.` : null,
		].filter(Boolean);
		return parts.length ? `${parts.join(' ')} You can still clock out.` : null;
	}, [allAssignedRunsComplete, allStopsHandled, couldNotCompleteDockets, remainingStops, sortedDockets, steps]);

	const getStepAfter = useCallback((key) => {
		const index = steps.findIndex(step => step.key === key);
		return index < 0
			? null
			: steps.slice(index + 1).find(step => step.status === MY_DAY_STEP_STATUS.TODO) || null;
	}, [steps]);

	const getStepPosition = useCallback((key) => {
		const index = steps.findIndex(step => step.key === key);
		return index < 0 ? null : { number: index + 1, total: steps.length };
	}, [steps]);

	return {
		steps,
		progress,
		currentKey,
		getStepAfter,
		getStepPosition,
		returnKeyStops,
		areKeysReturned,
		clockOutWarning,
	};
};

export default useEndOfDaySteps;
